import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight } from 'lucide-react';
import type { RGPDConfig } from '../../types/rgpd';
import type { SupportedLanguages } from '../../i18n';
import { CookiePreferences } from './CookiePreferences';
import { PrivacyPolicyModal } from '../privacy/PrivacyPolicyModal';
import { usePrivacyPolicy } from '../../hooks/usePrivacyPolicy';
import { useTranslatedContent } from '../../hooks/useTranslatedContent';

interface CookieBannerProps {
  config: RGPDConfig[];
  appConfig: any;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSavePreferences: (preferences: { analytics: boolean }) => void;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({
  config,
  appConfig,
  onAcceptAll,
  onRejectAll,
  onSavePreferences
}) => {
  const { i18n } = useTranslation();
  const [showPreferences, setShowPreferences] = useState(false);
  const currentLanguage = i18n.language as SupportedLanguages;
  const { 
    policy, 
    isModalOpen, 
    openModal, 
    closeModal,
    getLastUpdate 
  } = usePrivacyPolicy();

  // Get translations from trad_txt
  const { translations } = useTranslatedContent({
    terms: ['privacy_policy', 'cookie_preferences', 'cookie_reject', 'cookie_accept']
  });

  const getConfigText = (key: string, type: 'title' | 'description' = 'title'): string => {
    const item = config.find(c => c.key === key);
    if (!item) return key;

    const langKey = `${type}_${currentLanguage}` as keyof RGPDConfig;
    return (item[langKey] as string) || (type === 'title' ? item.title_fr : item.description_fr);
  };

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[currentLanguage] || translation.fr || key;
  };

  const handleSavePreferences = (preferences: { analytics: boolean }) => {
    onSavePreferences(preferences);
    setShowPreferences(false);
  };

  if (showPreferences) {
    return (
      <CookiePreferences
        config={config}
        appConfig={appConfig}
        onClose={() => setShowPreferences(false)}
        onSave={handleSavePreferences}
      />
    );
  }

  return (
    <>
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-[400] p-4 border-t"
        style={{ borderTopColor: appConfig['medium-color'] }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-sm text-gray-700 mb-2">
                {getConfigText('cookie_banner', 'description')}
              </p>
              <button
                onClick={openModal}
                className="text-xs hover:underline"
                style={{ color: appConfig['major-color'] }}
              >
                {getTranslatedText('privacy_policy')}
              </button>
            </div>
            
            {/* Responsive button grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={() => setShowPreferences(true)}
                className="flex items-center justify-center px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors gap-2 text-sm"
                style={{ borderRadius: appConfig.border_radius }}
              >
                <span>{getTranslatedText('cookie_preferences')}</span>
                <ChevronRight size={16} />
              </button>
              
              <button
                onClick={onRejectAll}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors text-sm"
                style={{ borderRadius: appConfig.border_radius }}
              >
                {getTranslatedText('cookie_reject')}
              </button>
              
              <button
                onClick={onAcceptAll}
                className="px-4 py-2 text-white rounded-lg transition-colors text-sm"
                style={{ 
                  backgroundColor: appConfig['major-color'],
                  borderRadius: appConfig.border_radius 
                }}
              >
                {getTranslatedText('cookie_accept')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && policy && (
        <PrivacyPolicyModal
          isOpen={isModalOpen}
          onClose={closeModal}
          policy={policy}
          config={appConfig}
          lastUpdate={getLastUpdate()}
        />
      )}
    </>
  );
};